// src/App.js

import React, { useState, useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { logout } from './utils/auth';
import './App.css';
import { ProtectedRoute } from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import FloatingBidButton from './components/FloatingBidButton';
import BiddingPopup from './components/BiddingPopup';
import Login from './pages/Login';
import Register from './pages/Register';
import Members from './pages/Members';
import Groups from './pages/Groups';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import OrganiserTools from './pages/OrganiserTools';
import AdminHome from './pages/AdminHome';
import UserHome from './pages/UserHome';
import GroupMembers from './pages/GroupMembers';
import AutoBid from './pages/AutoBid';
import PremiumReport from './pages/PremiumReport';
import ApproveRequests from './pages/ApproveRequests';
import ManageAuction from './pages/ManageAuction';
import InviteJoin from './pages/InviteJoin';
import BankDetails from './pages/BankDetails';
import InviteMembers from './pages/InviteMembers';
import EditProfile from './pages/EditProfile';
import ChangePassword from './pages/ChangePassword';
import GroupFormWrapper from './components/GroupForm';
import OrganiserRequest from './pages/OrganiserRequest';
import GroupCreate from './pages/GroupCreate';
import GroupEdit from './pages/GroupEdit';
import MyPayments from './pages/MyPayments';
import PaymentsReview from './pages/PaymentsReview';
import AdminApprovals from './pages/AdminApprovals';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Notifications from './pages/Notifications';
import Messages from './pages/Messages';
import DirectChat from './pages/DirectChat';
import GroupChat from './pages/GroupChat';
import NotificationBell from './components/NotificationBell';
import UserMenu from './components/UserMenu';
import { refreshSocketAuth, refreshSessionUser } from './lib/api';
import { socket, apiFetch, API_BASE } from './lib/api';




function BottomNavGate() {
  const { pathname } = useLocation();

  // Only hide on auth pages.
  const hide =
    pathname === '/login' ||
    pathname === '/register';

  if (hide) return null;
  return <BottomNav />;
}

// import Home from './pages/Home'; // removed: unused


// ✅ Socket refresh after login/reload

function App() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // ---------------------------------------------------------------------------
  // App version / cache safety (prevents stale mobile PWA bundles after deploy)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/version`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        const v = data?.version ? String(data.version) : null;
        if (!v || cancelled) return;
        const key = '__chitconnect_version__';
        const prev = localStorage.getItem(key);
        if (prev && prev !== v) {
          // New deploy detected: clear SW + caches then hard reload
          try {
            if ('serviceWorker' in navigator) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
            }
            if (window.caches && caches.keys) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
            }
          } catch {}
          localStorage.setItem(key, v);
          window.location.reload();
          return;
        }
        if (!prev) localStorage.setItem(key, v);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);


  // Global active bidding indicator (floating button)
  const [myGroups, setMyGroups] = useState([]);
  const [activeBids, setActiveBids] = useState([]);
  const [showGlobalBidPopup, setShowGlobalBidPopup] = useState(false);
  const [globalBidGroupId, setGlobalBidGroupId] = useState(null);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) setMobileOpen((p) => !p);
    else setCollapsed((p) => !p);
  };

  // Close mobile drawer on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initial user load + session hydration + socket auth refresh
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const storedUserStr = localStorage.getItem('user') || sessionStorage.getItem('user');
        if (storedUserStr) {
          try { if (!cancelled) setUser(JSON.parse(storedUserStr) || null); } catch { if (!cancelled) setUser(null); }
        }

        const hasToken = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
        const hasUser = !!storedUserStr;

        // If we have any hint of a session (token or stored user), confirm / hydrate it once up-front.
        if (hasToken || hasUser) {
          const me = await refreshSessionUser().catch(() => null);
          if (!cancelled && me) setUser(me);
        }

        // Ensure Socket.IO auth is aligned with the latest token/session marker.
        if (hasToken || (localStorage.getItem('token') || sessionStorage.getItem('token'))) {
          refreshSocketAuth();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Keep App-level user state in sync with ProtectedRoute (which fetches /api/me)
  useEffect(() => {
    const sync = () => {
      const stored = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (stored) {
        try { setUser(JSON.parse(stored) || null); } catch { setUser(null); }
      }
    };
    window.addEventListener('auth-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('auth-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);


  // Listen for server-pushed notifications (match the server event name)
  useEffect(() => {
    // Server emits 'notification' (see server/App.js notifyUser)
    const EVENT = 'notification';

    const onPush = (payload) => {
      // payload shape from server App.js notifyUser():
      // { id, type, title, message|body, url|data.href, created_at, ... }
      const msg = payload?.title ?? payload?.message ?? '';
      if (msg) {
        toast.info(msg, { autoClose: 5000 });
      }
      // Optionally bump the unread badge:
      apiFetch('/api/notifications/unread-count')
        .then(r => r.json())
        .then(({ count }) => {
          // TODO: update whatever state you use for header badge
          // e.g., setUnread(count);
        })
        .catch(() => {});
    };

    socket.on(EVENT, onPush);
    return () => socket.off(EVENT, onPush);
  }, []);

  // Keep body class in sync with mobile drawer
  useEffect(() => {
    document.body.classList.toggle('sidebar-open', mobileOpen);
  }, [mobileOpen]);

  // Keep socket auth fresh whenever the logged-in user changes
  useEffect(() => {
    if (user && sessionStorage.getItem('token')) refreshSocketAuth();
  }, [user]);

  // Sync across tabs (login/logout elsewhere)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'user') setUser(e.newValue ? JSON.parse(e.newValue) : null);
      if (e.key === 'token' && !e.newValue) setUser(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const onAny = (evt, ...args) => console.log('[socket event]', evt, args.length === 1 ? args[0] : args);
    socket.onAny(onAny);
    return () => socket.offAny(onAny);
  }, []);

  // Global bidding indicator polling (shows floating bid button)
  useEffect(() => {
    let alive = true;
    let timer = null;

    const run = async () => {
      if (!user) return;
      const groupsList = await fetchMyGroupsForIndicator();
      if (!alive) return;
      setMyGroups(groupsList);
      await refreshActiveBids(groupsList);
    };

    run();

    // Poll every 25s while logged in (lightweight)
    if (user) {
      timer = setInterval(run, 25000);
    }

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [user]);

  // Refresh indicator quickly on key socket events
  useEffect(() => {
    if (!user) return;
    const kick = () => refreshActiveBids(myGroups);

    const events = ['newBid', 'bidPlaced', 'auctionExtended', 'winnerFinalized'];
    events.forEach((ev) => socket.on(ev, kick));
    return () => events.forEach((ev) => socket.off(ev, kick));
  }, [user, myGroups]);




  const safeJson = async (res) => {
    try { return await res.json(); } catch { return null; }
  };

  const fetchMyGroupsForIndicator = async () => {
    try {
      const res = await apiFetch('/api/groups/my');
      if (!res.ok) return [];
      const data = await safeJson(res);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const refreshActiveBids = async (groupsList) => {
    try {
      const list = Array.isArray(groupsList) ? groupsList : [];
      const ids = list
        .map(g => g?.group_id ?? g?.id)
        .filter(Boolean);

      if (!ids.length) {
        setActiveBids([]);
        return;
      }

      const results = await Promise.allSettled(
        ids.map(async (gid) => {
          const r = await apiFetch(`/api/bidding/status/${gid}`);
          const st = await safeJson(r);
          return { gid, status: st, ok: r.ok };
        })
      );

      const byId = new Map(list.map(g => [String(g?.group_id ?? g?.id), g]));
      const live = results
        .filter(x => x.status === 'fulfilled')
        .map(x => x.value)
        .filter(({ ok, status }) => ok && status)
        .filter(({ status }) =>
          status.auction_active === true &&
          status.is_active === true &&
          status.bidding_enabled === true &&
          status.is_organiser_benefit_period !== true
        )
        .map(({ gid, status }) => {
          const g = byId.get(String(gid)) || {};
          return {
            groupId: gid,
            groupName: g?.name || g?.group_name || status?.group_name || `Group ${gid}`,
            periodNumber: status?.period_number,
            endsAt: status?.end_time,
            isOrganiserBenefit: !!status?.is_organiser_benefit_period,
            status,
          };
        })
        .sort((a, b) => {
          const ta = a.endsAt ? Date.parse(a.endsAt) : Number.POSITIVE_INFINITY;
          const tb = b.endsAt ? Date.parse(b.endsAt) : Number.POSITIVE_INFINITY;
          return ta - tb;
        });

      setActiveBids(live);
    } catch {
      // don't spam errors; just keep last known state
    }
  };


  const handleLogout = () => {
    try { logout(); } catch {}
    setUser(null);
    // Let the app re-render and routes guard kick in
    window.dispatchEvent(new Event('auth-changed'));
    window.location.href = '/login';
  };

  // Map many possible shapes to one of: 'superadmin' | 'organiser' | 'user'
  const deriveRole = (u) => {
    if (!u) return 'user';
    const role = (u.access_level || u.role || '').toLowerCase();

    // superadmin / admin
    if (role === 'superadmin' || role === 'admin' || u.is_admin === true) return 'superadmin';

    // organiser
    if (role === 'organiser' || u.is_organiser === true || u.organiser === true) return 'organiser';

    // default to regular user for anything else ('member', '', undefined, etc.)
    return 'user';
  };

  const HomePage = () => {
    if (loading) return <div>Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    const role = deriveRole(user);
    if (role === 'superadmin') return <AdminHome user={user} handleLogout={handleLogout} />;
    return <UserHome user={user} handleLogout={handleLogout} />; // organiser + user by default
  };

  return (
    <ErrorBoundary>
      <Router>
        <div className="app-container">
          {/* Only show the Sidebar when logged in */}
          {user && (
            <>
              <Sidebar
                collapsed={collapsed}
                toggle={toggleSidebar}
                mobileOpen={mobileOpen}
                onLinkClick={() => setMobileOpen(false)}
                user={user}
                onLogout={handleLogout}
              />
              {mobileOpen && <div className="backdrop" onClick={() => setMobileOpen(false)}></div>}
              <button className="mobile-toggle" onClick={toggleSidebar}>☰</button>
            </>
          )}

          <main className={`main-content ${user && collapsed ? 'collapsed' : ''}`}>
            {user && (
              <div className="user-fab">
                <UserMenu user={user} />
              </div>
            )}
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<Login setUser={setUser} />} />
              <Route path="/register" element={<Register />} />

              <Route path="/dashboard" element={<ProtectedRoute><UserHome user={user} handleLogout={handleLogout} /></ProtectedRoute>} />
              <Route path="/members" element={<ProtectedRoute><Members /></ProtectedRoute>} />
              <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
              <Route path="/groups/:groupId" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
              {/* Legacy/notification deep links */}
              <Route path="/groups/:groupId/payments" element={<ProtectedRoute><MyPayments user={user} /></ProtectedRoute>} />
              <Route path="/groups/:groupId/manage-auction" element={<ProtectedRoute><ManageAuction /></ProtectedRoute>} />
              <Route path="/payments" element={<ProtectedRoute><MyPayments user={user} /></ProtectedRoute>} />
              <Route path="/payments/review" element={<ProtectedRoute><PaymentsReview user={user} /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/messages/dm/:conversationId" element={<ProtectedRoute><DirectChat /></ProtectedRoute>} />
              <Route path="/messages/dm/u/:userId" element={<ProtectedRoute><DirectChat /></ProtectedRoute>} />
              <Route path="/messages/group/:groupId" element={<ProtectedRoute><GroupChat /></ProtectedRoute>} />
              <Route
                path="/users"
                element={
                  <RoleProtectedRoute allowedRoles={['superadmin']}>
                    <UserManagement />
                  </RoleProtectedRoute>
                }
              />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/groups/new" element={<ProtectedRoute><GroupCreate /></ProtectedRoute>} />
              <Route
                path="/organiser-tools"
                element={
                  <RoleProtectedRoute allowedRoles={['organiser', 'superadmin']}>
                    <OrganiserTools />
                  </RoleProtectedRoute>
                }
              />
              <Route path="/groups/organiser-request" element={<OrganiserRequest />} />
              <Route path="/groups/new" element={<GroupCreate />} />
              <Route path="/groups/:id/edit" element={<GroupEdit />} />
              <Route path="/groups/:groupId/approve" element={<ProtectedRoute><ApproveRequests /></ProtectedRoute>} />
              <Route path="/groups/:groupId/auction" element={<ProtectedRoute><ManageAuction /></ProtectedRoute>} />
              <Route path="/groups/:groupId/members" element={<ProtectedRoute><GroupMembers /></ProtectedRoute>} />
              <Route path="/groups/:groupId/autobid" element={<ProtectedRoute><AutoBid /></ProtectedRoute>} />
              <Route path="/groups/:groupId/premium-report" element={<ProtectedRoute><PremiumReport /></ProtectedRoute>} />
              <Route
                path="/groups/:groupId/invite"
                element={
                  <ProtectedRoute>
                    <InviteMembers />
                  </ProtectedRoute>
                }
              />
              <Route path="/groups/:groupId/edit" element={<ProtectedRoute><GroupFormWrapper mode="edit" /></ProtectedRoute>} />

              {/* 🔎 New: Super Admin Approvals screen */}
              <Route
                path="/admin/approvals"
                element={
                  <RoleProtectedRoute allowedRoles={['superadmin']}>
                    <AdminApprovals />
                  </RoleProtectedRoute>
                }
              />
               {/* Open routes for invite flow */}
              <Route path="/invite/:token" element={<InviteJoin />} />
              <Route path="/accept-invite/:token" element={<InviteJoin />} />
              <Route path="/invitation/:token" element={<InviteJoin />} />
              <Route path="/join/:token" element={<InviteJoin />} />

              {/* Profile & security */}
              <Route path="/bank-details" element={<ProtectedRoute><BankDetails user={user} /></ProtectedRoute>} />
              <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
              <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

              {/* 404 */}
              <Route path="*" element={<div style={{ padding: 16 }}>Page not found.</div>} />
            </Routes>
            {user && (
              <FloatingBidButton
                activeBids={activeBids}
                onOpenBid={(groupId) => {
                  setGlobalBidGroupId(groupId);
                  setShowGlobalBidPopup(true);
                }}
              />
            )}

            {user && showGlobalBidPopup && (
              <BiddingPopup
                group={(myGroups || []).find(g => String(g?.group_id ?? g?.id) === String(globalBidGroupId)) || { id: globalBidGroupId }}
                user={user}
                statusOverride={(activeBids || []).find(b => String(b.groupId) === String(globalBidGroupId))?.status}
                onClose={() => setShowGlobalBidPopup(false)}
              />
            )}

            <BottomNavGate user={user} />
            <ToastContainer />
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;